import React from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useGetList } from 'react-admin';
import { US_STATES_RESOURCE_NAME } from '../../usStates/usStates.dataProvider';

/**
 * US States Select Component with search/filter capabilities
 * 
 * Features:
 * - Uses existing admin-panel states API
 * - Autocomplete with fuzzy search  
 * - Shows both state name and abbreviation
 * - Returns state name (full name) as value
 * - Material-UI styling
 * - Consistent with existing admin-panel components
 */
const StatesSelect = ({
  value,
  onChange,
  error,
  helperText,
  label = "State",
  required = false,
  placeholder = "Select or type state name",
  ...props
}) => {
  // Fetch states using the existing data provider
  const { data: states, isLoading } = useGetList(
    US_STATES_RESOURCE_NAME,
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'name', order: 'ASC' }
    }
  );

  // Convert states array to proper format
  const statesOptions = states ? Object.values(states) : [];

  // Find the selected state object by name
  const selectedState = statesOptions.find(state => state.name === value) || null;

  const handleChange = (event, newValue) => {
    if (newValue) {
      onChange(newValue.name);
    } else {
      onChange('');
    }
  };

  if (isLoading) {
    return (
      <TextField
        label={label}
        placeholder="Loading states..."
        disabled
        fullWidth
        {...props}
      />
    );
  }

  return (
    <Autocomplete
      value={selectedState}
      onChange={handleChange}
      options={statesOptions}
      getOptionLabel={(option) => option.name || ''}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <span style={{ marginRight: '8px', fontWeight: 'bold', color: '#666' }}>
            {option.code || option.name?.substring(0, 2)?.toUpperCase()}
          </span>
          {option.name}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          fullWidth
        />
      )}
      // Enable filtering by both name and potential code
      filterOptions={(options, { inputValue }) => {
        const filterValue = inputValue.toLowerCase();
        return options.filter(option =>
          option.name?.toLowerCase().includes(filterValue) ||
          option.code?.toLowerCase().includes(filterValue)
        );
      }}
      // Show "No options" when no matches
      noOptionsText="No states found"
      // Auto-highlight first option
      autoHighlight
      // Clear button
      clearOnEscape
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      {...props}
    />
  );
};

export default StatesSelect;
